import { useEffect, useState, useRef, useCallback } from "react"
import { flushSync } from "react-dom"
import RuleEngineBackend from "../services/RuleEngineBackend"
import PPUtils from "../../utils/PPUtils"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { ContextMenu } from "primereact/contextmenu"
import { Dialog } from "primereact/dialog"
import { useTranslation } from "react-i18next"
import PPDecimalInput from "../common/PPDecimalInput"
import { parseDecimalInputOrNull } from "../common/decimalInputConfig"
import Select from "react-select"
import PPColumnSelectModal from "../common/PPColumnSelectModal"
import { FilterMatchMode } from "primereact/api"
import { toast } from "react-toastify"
import {
    buildApiFilters,
    appendChannelFilter,
    mapSortField,
    NUMERIC_FILTER_PLACEHOLDER,
    formatSentinelNumber,
    formatRuleDisplay,
    isDateFilterField,
} from "../common/tableQueryUtils"
import PPColumnDateFilter from "../common/PPColumnDateFilter"
import { getPrimeCalendarConfig } from "../common/primeCalendarSetup"
import { resolveChannelId, selectChannel } from "../common/priceManagementChannelStorage"
import { isExportCountWithinLimit, MAX_TABLE_EXPORT_ROWS } from "../common/tableExportConfig"
import "../../styles/priceman.css"
import { loadStoredJson, saveStoredJson, normalizeSortState, loadStoredStringArray, mergeColumnLayout, removeStorageItem } from "../common/tableStateStorage"
import { persistUserSettingsLocalstorage } from "../../utils/userSettingsLocalstorage"
import PPMatchingStatusBanner from "../../components/matching/PPMatchingStatusBanner"

const STORAGE_ACTIVE = "priceManagementChecklistColumns"
const STORAGE_ORDER = "priceManagementChecklistColumnOrder"
const STORAGE_SORT = "priceManagementChecklistSort"
const DEFAULT_SORT = { sortField: null, sortOrder: "asc" }

function getDefaultColumns(t) {
     return [
          { field: "id", header: t("pricemanagement_page.price_checklist.table_header_id"), filterable: true, active: true },
          { field: "fromProductnr", header: t("pricemanagement_page.price_checklist.table_header_fromProductnr"), filterable: true, active: true },
          { field: "ean", header: t("pricemanagement_page.price_checklist.table_header_ean"), filterable: true, active: true },
          { field: "productRanking", header: t("pricemanagement_page.price_checklist.table_header_productRanking"), filterable: true, active: true },
          { field: "producer", header: t("pricemanagement_page.price_checklist.table_header_producer"), filterable: true, active: true },
          { field: "price", header: t("pricemanagement_page.price_checklist.table_header_price"), filterable: true, active: true },
          { field: "lastPrice", header: t("pricemanagement_page.price_checklist.table_header_lastPrice"), filterable: true, active: true },
          { field: "shopPrice", header: t("pricemanagement_page.price_checklist.table_header_shopPrice"), filterable: true, active: true },
          { field: "shopTotalPrice", header: t("pricemanagement_page.price_checklist.table_header_shopTotalPrice"), filterable: true, active: true },
          { field: "priceDiff", header: t("pricemanagement_page.price_checklist.table_header_priceDiff"), filterable: true, active: true },
          { field: "istNettoFaktor", header: t("pricemanagement_page.price_checklist.table_header_istNettoFaktor"), filterable: true, active: true },
          { field: "nettoFaktor", header: t("pricemanagement_page.price_checklist.table_header_nettoFaktor"), filterable: true, active: true },
          { field: "spacer1", header: t("pricemanagement_page.price_checklist.table_header_-"), filterable: false, active: false },
          { field: "ppCurrency", header: t("pricemanagement_page.price_checklist.table_header_ppCurrency.currency"), filterable: true, active: true },
          { field: "clientStock", header: t("pricemanagement_page.price_checklist.table_header_stock"), filterable: true, active: true },
          { field: "stock", header: t("pricemanagement_page.price_checklist.table_header_foreign_stock"), filterable: true, active: true },
          { field: "spacer2", header: t("pricemanagement_page.price_checklist.table_header_-"), filterable: false, active: false },
          { field: "coupon", header: t("pricemanagement_page.price_checklist.table_header_coupon"), filterable: true, active: true },
          { field: "ppShop", header: t("pricemanagement_page.price_checklist.table_header_ppShop"), filterable: true, active: true },
          { field: "provider", header: t("pricemanagement_page.price_checklist.table_header_provider"), filterable: true, active: true },
          { field: "spacer3", header: t("pricemanagement_page.price_checklist.table_header_-"), filterable: false, active: false },
          { field: "spacer4", header: t("pricemanagement_page.price_checklist.table_header_-"), filterable: false, active: false },
          { field: "lang", header: t("pricemanagement_page.price_checklist.table_header_lang"), filterable: true, active: true },
          { field: "productname", header: t("pricemanagement_page.price_checklist.table_header_productname"), filterable: true, active: true },
          { field: "foreignShopProductName", header: t("pricemanagement_page.price_checklist.table_header_foreignShopProductName"), filterable: true, active: true },
          { field: "rule", header: t("pricemanagement_page.price_checklist.table_header_rule.name"), filterable: true, active: true },
          { field: "crawlDate", header: t("pricemanagement_page.price_checklist.table_header_crawlDate"), filterable: true, active: true },
          { field: "createDate", header: t("pricemanagement_page.price_checklist.table_header_createDate"), filterable: true, active: true },
          { field: "status", header: t("pricemanagement_page.price_checklist.table_header_status"), filterable: true, active: true },
          { field: "reason", header: t("pricemanagement_page.price_checklist.table_header_reason"), filterable: true, active: true },
          { field: "staging", header: t("pricemanagement_page.price_checklist.table_header_staging"), filterable: true, active: true },
     ]
}

function buildFiltersState() {
     const keys = [
          "id",
          "fromProductnr",
          "ean",
          "productRanking",
          "producer",
          "price",
          "lastPrice",
          "shopPrice",
          "shopTotalPrice",
          "priceDiff",
          "istNettoFaktor",
          "nettoFaktor",
          "spacer1",
          "ppCurrency",
          "clientStock",
          "stock",
          "spacer2",
          "coupon",
          "ppShop",
          "provider",
          "spacer3",
          "spacer4",
          "lang",
          "productname",
          "foreignShopProductName",
          "rule",
          "crawlDate",
          "createDate",
          "status",
          "reason",
          "staging",
     ]
     const o = {}
     keys.forEach((k) => {
          o[k] = { value: null, matchMode: k === "fromProductnr" ? "startsWith" : "contains" }
     })
     return o
}

function migrateStoredField(f) {
     const leg = { "-": "spacer1", "--": "spacer2", "---": "spacer3", "----": "spacer4" }
     return leg[f] || f
}

function loadColumnsFromStorage(t) {
     const defaults = getDefaultColumns(t)
     const savedOrderRaw = loadStoredStringArray(STORAGE_ORDER)
     const savedActiveRaw = loadStoredStringArray(STORAGE_ACTIVE)
     const savedOrder = savedOrderRaw == null ? null : savedOrderRaw.map(migrateStoredField)
     const savedActive = savedActiveRaw == null ? null : savedActiveRaw.map(migrateStoredField)
     return mergeColumnLayout(defaults, savedOrder, savedActive)
}

function getSortableFields(t) {
     return getDefaultColumns(t).map((c) => mapSortField(c.field) || c.field)
}

function loadSortFromStorage(t) {
     return normalizeSortState(loadStoredJson(STORAGE_SORT, null), getSortableFields(t), DEFAULT_SORT)
}

function getChecklistIds(rows) {
     const ids = (rows || [])
          .map((row) => (row && typeof row === "object" ? row.id : row))
          .map((id) => Number(id))
          .filter((id) => Number.isSafeInteger(id) && id > 0)
     return [...new Set(ids)]
}

export default function PPPriceManagementChecklist() {
     const auth = PPUtils.getLogin()
     const token = auth.token
     const customerId = PPUtils.getEffectiveCustomerId(auth)
     const userId = auth.user?.id
     const BATCH_SIZE = 50
     const INITIAL_PAGES = 6
     const ROW_HEIGHT = 40
     const PREFETCH_PAGES = 6
     const PREFETCH_VIEWPORTS = 8
     const PREFETCH_MIN_DISTANCE = 4000
     const { t, i18n } = useTranslation()
     const cm = useRef(null)
     const tableWrapRef = useRef(null)
     const nextPageRef = useRef(0)
     const loadingMoreRef = useRef(false)

     const [columns, setColumns] = useState(() => loadColumnsFromStorage(t))
     const [channels, setChannels] = useState([])
     const [selectedChannelID, setSelectedChannelID] = useState(null)
     const [departmentOptions, setDepartmentOptions] = useState([])
     const [selectedDepartments, setSelectedDepartments] = useState([])
     const [editFilter, setEditFilter] = useState(false)
     const [errorFilter, setErrorFilter] = useState(false)
     const [specialRulesFilter, setSpecialRulesFilter] = useState(false)
     const [tableData, setTableData] = useState([])
     const [discontinuedItemsOption, setDiscontinuedItemsOption] = useState(0)
     const [totalRecords, setTotalRecords] = useState(0)
     const [loading, setLoading] = useState(false)
     const [backgroundLoading, setBackgroundLoading] = useState(false)
     const [columnModalVisible, setColumnModalVisible] = useState(false)
     const [sortField, setSortField] = useState(() => loadSortFromStorage(t).sortField)
     const [sortOrder, setSortOrder] = useState(() => loadSortFromStorage(t).sortOrder)
     const [filters, setFilters] = useState(buildFiltersState)
     const [selectedRows, setSelectedRows] = useState(null)
     const [contextMenuSelection, setContextMenuSelection] = useState(null)
     const [editPriceOpen, setEditPriceOpen] = useState(false)
     const [editPriceRow, setEditPriceRow] = useState(null)
     const [editPriceValue, setEditPriceValue] = useState(null)
     const [editNettoValue, setEditNettoValue] = useState(null)
     const [editBruttoValue, setEditBruttoValue] = useState(null)
     const [editBulkMode, setEditBulkMode] = useState(false)
     const [refusedExporting, setRefusedExporting] = useState(false)
     const [confirmApplyOpen, setConfirmApplyOpen] = useState(false)
     const [confirmRejectOpen, setConfirmRejectOpen] = useState(false)
     const [confirmEditPriceOpen, setConfirmEditPriceOpen] = useState(false)
     const [pendingEditPrice, setPendingEditPrice] = useState(null)
     const [selectingAll, setSelectingAll] = useState(false)
     const [actionPending, setActionPending] = useState(false)

     const getInitialPageCount = useCallback((total) => {
          const wrap = tableWrapRef.current
          let minRows = BATCH_SIZE
          if (wrap) {
               const thead = wrap.querySelector(".p-datatable-thead")
               const wrapper = wrap.querySelector(".p-datatable-wrapper") || wrap
               const availableHeight = wrapper.clientHeight - (thead?.offsetHeight ?? ROW_HEIGHT * 2)
               if (availableHeight > 0) {
                    minRows = Math.ceil(availableHeight / ROW_HEIGHT) + 5
               }
          }
          const pagesForViewport = Math.ceil(minRows / BATCH_SIZE)
          return Math.min(Math.ceil(total / BATCH_SIZE), Math.max(INITIAL_PAGES, pagesForViewport))
     }, [])

     useEffect(() => {
          const defs = getDefaultColumns(t)
          const h = Object.fromEntries(defs.map((c) => [c.field, c.header]))
          setColumns((prev) => prev.map((c) => ({ ...c, header: h[c.field] || c.header })))
     }, [t])

     const buildAllFilters = useCallback(() => {
          const allFilters = {}
          if (discontinuedItemsOption === 1) {
               allFilters.hideDiscontinued = true
          } else if (discontinuedItemsOption === 2) {
               allFilters.onlyDiscontinued = true
          }
          if (editFilter) {
               allFilters.editFilter = true
          }
          if (errorFilter) {
               allFilters.errorFilter = true
          }
          if (specialRulesFilter) {
               allFilters.specialRulesFilter = true
          }
          if (selectedDepartments.length === 1) {
               allFilters["department:EQUALS"] = selectedDepartments[0].value
          } else if (selectedDepartments.length > 1) {
               allFilters["department:IN"] = selectedDepartments.map((department) => department.value).join(",")
          }
          const apiFilters = buildApiFilters(filters, { extra: allFilters })
          return appendChannelFilter(apiFilters, selectedChannelID)
     }, [discontinuedItemsOption, editFilter, errorFilter, specialRulesFilter, filters, selectedChannelID, selectedDepartments])

     const fetchRange = useCallback(
          async (startPage, pageCount) => {
               if (pageCount <= 0) {
                    return []
               }
               const allFilters = buildAllFilters()
               const pages = Array.from({ length: pageCount }, (_, i) => startPage + i)
               const results = await Promise.all(
                    pages.map((page) =>
                         RuleEngineBackend.getChecklistItems(
                              token,
                              page,
                              BATCH_SIZE,
                              allFilters,
                              mapSortField(sortField),
                              sortOrder
                         )
                    )
               )
               const rows = []
               results.forEach((items) => {
                    (items ?? []).forEach((item) => rows.push(item))
               })
               return rows
          },
          [token, buildAllFilters, sortField, sortOrder]
     )

     const loadMore = useCallback(async () => {
          if (loadingMoreRef.current) {
               return
          }
          if (totalRecords > 0 && tableData.length >= totalRecords) {
               return
          }
          loadingMoreRef.current = true
          setBackgroundLoading(true)
          try {
               const startPage = nextPageRef.current
               const remaining = totalRecords - tableData.length
               const pagesLeft = Math.ceil(remaining / BATCH_SIZE)
               const pageCount = Math.min(PREFETCH_PAGES, pagesLeft)
               const rows = await fetchRange(startPage, pageCount)
               if (rows.length > 0) {
                    setTableData((prev) => [...prev, ...rows])
                    nextPageRef.current = startPage + pageCount
               }
          } finally {
               loadingMoreRef.current = false
               setBackgroundLoading(false)
          }
     }, [tableData.length, totalRecords, fetchRange])

     useEffect(() => {
          let cancelled = false
          nextPageRef.current = 0
          setTableData([])
          setSelectedRows(null)
          setLoading(true)

          async function resetList() {
               if (selectedChannelID == null) {
                    setTableData([])
                    setTotalRecords(0)
                    setLoading(false)
                    return
               }
               const allFilters = buildAllFilters()
               const count = await RuleEngineBackend.getChecklistItemsCount(token, allFilters)
               if (cancelled) {
                    return
               }
               const total = count ?? 0
               setTotalRecords(total)
               if (total > 0) {
                    const pageCount = getInitialPageCount(total)
                    const rows = await fetchRange(0, pageCount)
                    if (!cancelled) {
                         setTableData(rows)
                         nextPageRef.current = pageCount
                    }
               }
               if (!cancelled) {
                    setLoading(false)
               }
          }

          resetList()
          return () => {
               cancelled = true
          }
     }, [token, customerId, buildAllFilters, sortField, sortOrder, fetchRange, getInitialPageCount, selectedChannelID])

     useEffect(() => {
          async function fetchChannels() {
               const allChannels = await RuleEngineBackend.getChannels(customerId, token)
               if (allChannels) {
                    const channelList = Array.isArray(allChannels) ? allChannels : [allChannels]
                    const visible = channelList.filter((channel) => !channel.hidden)
                    setChannels(visible.length > 0 ? visible : channelList)
                    if (selectedChannelID == null || !channelList.some((channel) => Number(channel.id) === Number(selectedChannelID))) {
                         const resolvedChannelId = resolveChannelId(channelList, customerId, selectedChannelID)
                         setSelectedChannelID(resolvedChannelId)
                    }
               }
          }
          fetchChannels()
     }, [customerId, token])

     useEffect(() => {
          async function fetchDepartments() {
               const topCategories = await RuleEngineBackend.getTopCategories(token, customerId)
               if (Array.isArray(topCategories)) {
                    const departments = topCategories
                         .filter((category) => category?.catShort)
                         .map((category) => ({
                              value: category.catShort,
                              label: category.name || category.catShort,
                         }))
                    setDepartmentOptions([
                         {
                              value: "all_departments",
                              label: "- Alle Abteilungen -",
                              isAllOption: true,
                              allEntries: departments,
                         },
                         ...departments,
                    ])
               }
          }
          fetchDepartments()
     }, [customerId, token])

     const checklistScrollEl = () => {
          const root = tableWrapRef.current
          if (!root) {
               return null
          }
          const inner = root.querySelector(".p-datatable-wrapper")
          if (inner && inner.scrollHeight > inner.clientHeight + 1) {
               return inner
          }
          return root
     }

     useEffect(() => {
          const wrap = checklistScrollEl()
          if (!wrap) {
               return
          }
          const onScroll = () => {
               const threshold = Math.max(PREFETCH_MIN_DISTANCE, wrap.clientHeight * PREFETCH_VIEWPORTS)
               if (wrap.scrollTop + wrap.clientHeight >= wrap.scrollHeight - threshold) {
                    loadMore()
               }
          }
          wrap.addEventListener("scroll", onScroll, { passive: true })
          return () => wrap.removeEventListener("scroll", onScroll)
     }, [loadMore, tableData.length, loading])

     useEffect(() => {
          if (loading || tableData.length >= totalRecords) {
               return
          }
          const wrap = checklistScrollEl()
          if (!wrap) {
               return
          }
          if (wrap.scrollHeight <= wrap.clientHeight + 1) {
               loadMore()
          }
     }, [tableData.length, totalRecords, loading, loadMore])

     const refreshList = useCallback(async () => {
          setSelectedRows(null)
          nextPageRef.current = 0
          setLoading(true)
          if (selectedChannelID == null) {
               setTableData([])
               setTotalRecords(0)
               setLoading(false)
               return
          }
          const allFilters = buildAllFilters()
          const count = await RuleEngineBackend.getChecklistItemsCount(token, allFilters)
          const total = count ?? 0
          setTotalRecords(total)
          if (total > 0) {
               const pageCount = getInitialPageCount(total)
               const rows = await fetchRange(0, pageCount)
               setTableData(rows)
               nextPageRef.current = pageCount
          } else {
               setTableData([])
          }
          setLoading(false)
     }, [token, buildAllFilters, fetchRange, getInitialPageCount, selectedChannelID])

     const visibleColumns = columns.filter((c) => c.active)
     const globalFilterFields = visibleColumns.filter((c) => c.filterable).map((c) => c.field)

     const setTableColumns = (selection) => {
          const selectedFields = selection.map((sel) => sel.value)
          const selectedSet = new Set(selectedFields)
          const byField = Object.fromEntries(columns.map((c) => [c.field, c]))
          const ordered = []
          selectedFields.forEach((f) => {
               if (byField[f]) {
                    ordered.push({ ...byField[f], active: true })
               }
          })
          columns.forEach((c) => {
               if (!selectedSet.has(c.field)) {
                    ordered.push({ ...c, active: false })
               }
          })
          setColumns(ordered)
          saveStoredJson(STORAGE_ACTIVE, selectedFields)
          saveStoredJson(STORAGE_ORDER, ordered.map((c) => c.field))
          persistUserSettingsLocalstorage()
     }

     const resetColumns = () => {
          const defs = getDefaultColumns(t)
          setColumns(defs)
          removeStorageItem(STORAGE_ACTIVE)
          removeStorageItem(STORAGE_ORDER)
          persistUserSettingsLocalstorage()
     }

     const resetFilters = () => {
          setFilters(buildFiltersState())
          setEditFilter(false)
          setErrorFilter(false)
          setSpecialRulesFilter(false)
          setDiscontinuedItemsOption(0)
          setSelectedDepartments([])
          setSortField(DEFAULT_SORT.sortField)
          setSortOrder(DEFAULT_SORT.sortOrder)
          saveStoredJson(STORAGE_SORT, DEFAULT_SORT)
          persistUserSettingsLocalstorage()
     }

     const onColumnReorder = (e) => {
          const visibleIndices = columns.reduce((acc, c, i) => {
               if (c.active) {
                    acc.push(i)
               }
               return acc
          }, [])
          const from = visibleIndices[e.dragIndex]
          const to = visibleIndices[e.dropIndex]
          if (from === undefined || to === undefined) {
               return
          }
          const next = [...columns]
          const [moved] = next.splice(from, 1)
          next.splice(to, 0, moved)
          setColumns(next)
          saveStoredJson(STORAGE_ORDER, next.map((c) => c.field))
          persistUserSettingsLocalstorage()
     }

     const renderColumnFilter = (field, header, filterable) => {
          if (!filterable) {
               return <span className="text-muted small">—</span>
          }
          if (field === "clientStock" || field === "stock") {
               return (
                    <select
                         value={filters[field]?.value || ""}
                         onChange={(e) => onFilterChange(e, field)}
                         className="form-select form-select-sm"
                         style={{ width: "100%", fontSize: "12px" }}>
                         <option value="">{header}</option>
                         <option value="green">green</option>
                         <option value="red">red</option>
                         <option value="yellow">yellow</option>
                    </select>
               )
          }
          if (isDateFilterField(field)) {
               const { locale, dateFormat } = getPrimeCalendarConfig(i18n.language)
               return (
                    <PPColumnDateFilter
                         value={filters[field]?.value}
                         onChange={(value) => onDateFilterChange(field, value)}
                         placeholder={header}
                         locale={locale}
                         dateFormat={dateFormat}
                    />
               )
          }
          const isNumeric = [
               "id", "price", "lastPrice", "shopPrice", "shopTotalPrice", "priceDiff",
               "istNettoFaktor", "nettoFaktor", "bruttoFaktor", "productRanking", "coupon",
          ].includes(field)
          return (
               <input
                    type="text"
                    value={filters[field]?.value || ""}
                    onChange={(e) => onFilterChange(e, field)}
                    placeholder={isNumeric ? NUMERIC_FILTER_PLACEHOLDER : header}
                    className="p-inputtext p-column-filter"
                    style={{
                         width: "100%",
                         fontSize: "12px",
                         padding: "6px 8px",
                         border: "1px solid #ced4da",
                         borderRadius: "4px",
                    }}
               />
          )
     }

     const onFilterChange = (e, field) => {
          const value = e.target.value
          setFilters((prev) => ({
               ...prev,
               [field]: { value, matchMode: field === "fromProductnr" ? "startsWith" : "contains" },
          }))
     }

     const onDateFilterChange = (field, value) => {
          setFilters((prev) => ({
               ...prev,
               [field]: { value: value ?? null, matchMode: "custom" },
          }))
     }

     const onFilter = (e) => {
          setFilters(e.filters)
     }

     const onSort = (event) => {
          let nextField
          let nextOrder
          if (event.sortField) {
               nextField = event.sortField
               nextOrder = event.sortOrder === 1 ? "asc" : "desc"
          } else {
               nextField = null
               nextOrder = "asc"
          }
          setSortField(nextField)
          setSortOrder(nextOrder)
          saveStoredJson(STORAGE_SORT, { sortField: nextField, sortOrder: nextOrder })
          persistUserSettingsLocalstorage()
     }

     const rowClassName = (data) => {
          if (data.staging === true || data.staging === "true" || data.staging === 1) {
               return { "table-info": true }
          }
          return null
     }

     const openEditPrice = (row) => {
          const targets = selectedRows?.length > 1 ? selectedRows : row ? [row] : []
          if (targets.length === 0) {
               return
          }
          setEditBulkMode(targets.length > 1)
          setEditPriceRow(targets[0])
          const p = targets[0].price != null ? Number(targets[0].price) : null
          setEditPriceValue(Number.isFinite(p) ? p : null)
          setEditNettoValue(null)
          setEditBruttoValue(null)
          setEditPriceOpen(true)
     }

     const requestSaveEditedPrice = () => {
          const targets = editBulkMode && selectedRows?.length > 1 ? selectedRows : editPriceRow ? [editPriceRow] : []
          if (targets.length === 0) {
               return
          }
          if (!editBulkMode && editPriceValue == null) {
               return
          }
          if (editBulkMode && editNettoValue == null && editBruttoValue == null && editPriceValue == null) {
               return
          }
          setPendingEditPrice({ targets, editBulkMode })
          setEditPriceOpen(false)
          setConfirmEditPriceOpen(true)
     }

     const runConfirmedEditPrice = async () => {
          if (!pendingEditPrice) {
               return
          }
          const { targets, editBulkMode: bulk } = pendingEditPrice
          const ids = getChecklistIds(targets)
          if (ids.length !== targets.length || userId == null) {
               toast.error(t("pricemanagement_page.price_checklist.invalid_selection_error"))
               return
          }
          setActionPending(true)
          try {
               if (bulk && (editNettoValue != null || editBruttoValue != null)) {
                    const isNetto = editNettoValue != null
                    const factor = isNetto ? editNettoValue : editBruttoValue
                    await RuleEngineBackend.postChecklistAdaptAndSave(token, ids, userId, isNetto, factor, editPriceValue)
               } else if (!bulk && targets.length === 1) {
                    const item = {
                         ...targets[0],
                         price: editPriceValue,
                         nettoFaktor: editNettoValue ?? targets[0].nettoFaktor,
                         bruttoFaktor: editBruttoValue ?? targets[0].bruttoFaktor,
                    }
                    await RuleEngineBackend.postChecklistAcceptSingle(token, item, userId)
               } else {
                    await RuleEngineBackend.postChecklistAdaptAndSave(token, ids, userId, false, null, editPriceValue)
               }
               toast.success(t("pricemanagement_page.price_checklist.edit_price_success"))
               setConfirmEditPriceOpen(false)
               setPendingEditPrice(null)
               setSelectedRows(null)
               refreshList()
          } catch (error) {
               toast.error(`${t("pricemanagement_page.price_checklist.edit_price_error")}: ${error.message}`)
          } finally {
               setActionPending(false)
          }
     }

     const runAccept = async () => {
          const ids = getChecklistIds(selectedRows)
          if (ids.length === 0 || ids.length !== (selectedRows || []).length || userId == null) {
               toast.error(t("pricemanagement_page.price_checklist.invalid_selection_error"))
               return
          }
          setActionPending(true)
          try {
               await RuleEngineBackend.postChecklistAccept(token, ids, userId)
               toast.success(t("single.updated"))
               setConfirmApplyOpen(false)
               refreshList()
          } catch (error) {
               toast.error(`${t("pricemanagement_page.price_checklist.accept_error")}: ${error.message}`)
          } finally {
               setActionPending(false)
          }
     }

     const runReject = async () => {
          const ids = getChecklistIds(selectedRows)
          if (ids.length === 0 || ids.length !== (selectedRows || []).length || userId == null) {
               toast.error(t("pricemanagement_page.price_checklist.invalid_selection_error"))
               return
          }
          setActionPending(true)
          try {
               await RuleEngineBackend.postChecklistReject(token, ids, userId)
               toast.success(t("single.updated"))
               setConfirmRejectOpen(false)
               refreshList()
          } catch (error) {
               toast.error(`${t("pricemanagement_page.price_checklist.reject_error")}: ${error.message}`)
          } finally {
               setActionPending(false)
          }
     }

     const exportCsv = async () => {
          if (!isExportCountWithinLimit(totalRecords)) {
               toast.error(t("pricemanagement_page.price_checklist.export_limit_exceeded", {
                    count: totalRecords,
                    max: MAX_TABLE_EXPORT_ROWS,
               }))
               return
          }
          const result = await RuleEngineBackend.getChecklistAsCSV(customerId, token, buildAllFilters())
          if (result?.success) {
               toast.success(t("pricemanagement_page.price_checklist.export_started"))
          } else {
               toast.error(t("pricemanagement_page.price_checklist.export_failed", {
                    message: result?.error || t("pricemanagement_page.overview_page.toast_error"),
               }))
          }
     }

     const exportRefused = async () => {
          if (refusedExporting) {
               return
          }
          flushSync(() => {
               setRefusedExporting(true)
          })
          await new Promise((resolve) => {
               requestAnimationFrame(() => requestAnimationFrame(resolve))
          })
          try {
               const result = await RuleEngineBackend.getRefusedPricesAsCSV(customerId, token)
               if (result?.cancelled) {
                    return
               }
               if (result?.success) {
                    toast.success(t("pricemanagement_page.price_checklist.export_started"))
               } else {
                    toast.error(t("pricemanagement_page.price_checklist.export_failed", {
                         message: result?.error || t("pricemanagement_page.overview_page.toast_error"),
                    }))
               }
          } finally {
               setRefusedExporting(false)
          }
     }

     const onTableKeyDown = useCallback(async (e) => {
          const target = e.target
          const isEditable =
               target instanceof HTMLElement &&
               (target.isContentEditable ||
                    ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
          if (isEditable) return

          if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a") {
               e.preventDefault()
               if (selectingAll) {
                    return
               }
               if (selectedRows?.length === totalRecords && totalRecords > 0) {
                    setSelectedRows([])
                    return
               }
               setSelectingAll(true)
               try {
                    const result = await RuleEngineBackend.getChecklistItemIds(token, buildAllFilters())
                    if (!Array.isArray(result)) {
                         throw new Error(t("pricemanagement_page.price_checklist.invalid_ids_response"))
                    }
                    const ids = getChecklistIds(result)
                    if (ids.length !== result.length) {
                         throw new Error(t("pricemanagement_page.price_checklist.invalid_ids_response"))
                    }
                    const loadedById = new Map(tableData.map((row) => [Number(row.id), row]))
                    setSelectedRows(ids.map((id) => loadedById.get(id) || { id }))
               } catch (error) {
                    toast.error(`${t("pricemanagement_page.price_checklist.select_all_error")}: ${error.message}`)
               } finally {
                    setSelectingAll(false)
               }
          } else if (e.key === "Escape") {
               setSelectedRows([])
          }
     }, [buildAllFilters, selectedRows, selectingAll, t, tableData, token, totalRecords])

     useEffect(() => {
          document.addEventListener("keydown", onTableKeyDown)
          return () => document.removeEventListener("keydown", onTableKeyDown)
     }, [onTableKeyDown])

     const menuItems = [
          {
               label: t("pricemanagement_page.price_checklist.adjust_price_menu"),
               command: () => openEditPrice(contextMenuSelection || (selectedRows?.length === 1 ? selectedRows[0] : null)),
          },
     ]

     if (customerId == null) {
          return (
               <div className="container-fluid priceman-page">
                    <div className="card shadow">
                         <div className="card-body">
                              <PPMatchingStatusBanner variant="no_customer" />
                         </div>
                    </div>
               </div>
          )
     }

     return (
          <>
               <div className="container-fluid priceman-page">
                    <div className="row">
                         <div className="col-12">
                              <div className="card shadow">
                                   <div className="card-body">
                                        <div className="p-3 d-flex flex-column priceman-page-body">
                                             <div className="d-flex flex-row justify-content-between align-items-center flex-wrap gap-2">
                                                  <div className="d-flex flex-row align-items-center flex-wrap gap-3 mt-2 mb-2">
                                                       <Select
                                                                 className="priceman-channel-select"
                                                                 isClearable={false}
                                                                 value={
                                                                      selectedChannelID != null
                                                                           ? channels
                                                                                .map((channel) => ({
                                                                                     label: channel.channelName,
                                                                                     id: channel.id,
                                                                                     value: channel.id,
                                                                                }))
                                                                                .find((option) => option.id === selectedChannelID) || null
                                                                           : null
                                                                 }
                                                                 onChange={(event) => selectChannel(customerId, event ? event.id : null, setSelectedChannelID)}
                                                                  options={channels.map((channel) => ({
                                                                       label: channel.channelName,
                                                                       id: channel.id,
                                                                       value: channel.id,
                                                                  }))}
                                                        />
                                                        <Select
                                                                  className="priceman-department-select"
                                                                  isMulti
                                                                  isClearable
                                                                  placeholder={t("single.department")}
                                                                  options={departmentOptions}
                                                                  value={selectedDepartments}
                                                                  onChange={(value) => {
                                                                       const selected = value || []
                                                                       const allOption = selected.find((option) => option.isAllOption)
                                                                       setSelectedDepartments(allOption ? allOption.allEntries : selected)
                                                                  }}
                                                                  styles={{
                                                                       valueContainer: (provided) => ({
                                                                            ...provided,
                                                                            maxHeight: "100px",
                                                                            overflowY: "auto",
                                                                       }),
                                                                  }}
                                                        />
                                                        <div className="d-flex flex-row align-items-center">
                                                            <p className="text-md me-3 mb-0">{t("pricemanagement_page.price_checklist.discontinued_label")}</p>
                                                            <div className="btn-group" role="group">
                                                                 <button
                                                                      type="button"
                                                                      title={t("pricemanagement_page.price_checklist.discontinued_show_hint")}
                                                                      className={discontinuedItemsOption === 0 ? "btn btn-primary" : "btn btn-outline-primary"}
                                                                      onClick={() => setDiscontinuedItemsOption(0)}>
                                                                      {t("single.show")}
                                                                 </button>
                                                                 <button
                                                                      type="button"
                                                                      title={t("pricemanagement_page.price_checklist.discontinued_hide_hint")}
                                                                      className={discontinuedItemsOption === 1 ? "btn btn-primary" : "btn btn-outline-primary"}
                                                                      onClick={() => setDiscontinuedItemsOption(1)}>
                                                                      {t("single.hide")}
                                                                 </button>
                                                                 <button
                                                                      type="button"
                                                                      title={t("pricemanagement_page.price_checklist.discontinued_extract_hint")}
                                                                      className={discontinuedItemsOption === 2 ? "btn btn-primary" : "btn btn-outline-primary"}
                                                                      onClick={() => setDiscontinuedItemsOption(2)}>
                                                                      {t("single.extract")}
                                                                 </button>
                                                            </div>
                                                       </div>
                                                       <label className="form-check form-switch form-switch-inline">
<input
                                                                 className="form-check-input"
                                                                 type="checkbox"
                                                                 role="switch"
                                                                 checked={editFilter}
                                                                 onChange={() => setEditFilter(!editFilter)}
                                                            />
<span className="form-check-label text-md ms-1">{t("pricemanagement_page.price_checklist.filter_edit")}</span>
</label>
                                                       <label className="form-check form-switch form-switch-inline">
<input
                                                                 className="form-check-input"
                                                                 type="checkbox"
                                                                 role="switch"
                                                                 checked={errorFilter}
                                                                 onChange={() => setErrorFilter(!errorFilter)}
                                                            />
<span className="form-check-label text-md ms-1">{t("pricemanagement_page.price_checklist.filter_error")}</span>
</label>
                                                       <label className="form-check form-switch form-switch-inline">
<input
                                                                 className="form-check-input"
                                                                 type="checkbox"
                                                                 role="switch"
                                                                 checked={specialRulesFilter}
                                                                 onChange={() => setSpecialRulesFilter(!specialRulesFilter)}
                                                            />
<span className="form-check-label text-md ms-1">{t("pricemanagement_page.price_checklist.filter_special")}</span>
</label>
                                                  </div>
                                                  <div className="d-flex flex-row align-items-center flex-wrap" style={{ gap: "8px" }}>
                                                       <button
                                                            type="button"
                                                            className="btn btn-success btn-lg"
                                                            disabled={!selectedRows || selectedRows.length === 0 || actionPending || selectingAll}
                                                            onClick={() => setConfirmApplyOpen(true)}>
                                                            {t("pricemanagement_page.price_checklist.apply_prices")}
                                                       </button>
                                                       <button
                                                            type="button"
                                                            className="btn btn-danger btn-lg"
                                                            disabled={!selectedRows || selectedRows.length === 0 || actionPending || selectingAll}
                                                            onClick={() => setConfirmRejectOpen(true)}>
                                                            {t("pricemanagement_page.price_checklist.reject_prices")}
                                                       </button>
                                                       <button
                                                            type="button"
                                                            className="btn btn-outline-primary btn-lg"
                                                            onClick={exportCsv}
                                                            title={t("pricemanagement_page.price_checklist.export_csv")}>
                                                            <i className="fas fa-file-csv" />
                                                       </button>
                                                       <button
                                                            type="button"
                                                            className="btn btn-outline-primary btn-lg"
                                                            onClick={exportRefused}
                                                            disabled={refusedExporting}
                                                            title={t("pricemanagement_page.price_checklist.show_refused")}>
                                                            <i className={refusedExporting ? "fas fa-circle-notch fa-spin" : "fas fa-ban"} />
                                                       </button>
                                                       <button
                                                            type="button"
                                                            className="btn btn-outline-secondary btn-lg"
                                                            onClick={resetFilters}
                                                            title={t("pricemanagement_page.price_checklist.reset_filters")}>
                                                            <i className="fas fa-filter" />
                                                       </button>
                                                       <button
                                                            type="button"
                                                            className="btn btn-primary btn-lg"
                                                            onClick={() => setColumnModalVisible(true)}
                                                            title={t("pricemanagement_page.price_checklist.column_picker_title")}>
                                                            <i className="fas fa-columns" />
                                                       </button>
                                                       <button
                                                            type="button"
                                                            className="btn btn-outline-secondary btn-lg"
                                                            onClick={resetColumns}
                                                            title={t("pricemanagement_page.price_checklist.reset_columns")}>
                                                            <i className="fas fa-undo" />
                                                       </button>
                                                  </div>
                                             </div>
                                             <div
                                                  className="mt-2 w-100 priceman-table-row"
                                                  style={{ outline: "none" }}
                                                  role="region"
                                                  aria-label={t("pricemanagement_page.taps.checklist_tap_title")}>
                                                  <ContextMenu model={menuItems} ref={cm} onHide={() => setContextMenuSelection(null)} />
                                                  <div className="priceman priceman-table-wrap" ref={tableWrapRef}>
                                                       <DataTable
                                                            value={tableData}
                                                            globalFilterFields={globalFilterFields}
                                                            filters={filters}
                                                            onFilter={onFilter}
                                                            onSort={onSort}
                                                            sortField={sortField}
                                                            sortOrder={sortOrder === "asc" ? 1 : -1}
                                                            filterDisplay="row"
                                                            sortMode="single"
                                                            stripedRows
                                                            lazy
                                                            loading={loading}
                                                            size="small"
                                                            selection={selectedRows}
                                                            onSelectionChange={(e) => setSelectedRows(e.value)}
                                                            dataKey="id"
                                                            selectionMode="multiple"
                                                            metaKeySelection={true}
                                                            contextMenuSelection={contextMenuSelection}
                                                            onContextMenuSelectionChange={(e) => setContextMenuSelection(e.value)}
                                                            reorderableColumns
                                                            onColReorder={onColumnReorder}
                                                            scrollable
                                                            scrollHeight="flex"
                                                            resizableColumns
                                                            columnResizeMode="expand"
                                                            rowClassName={rowClassName}
                                                            onContextMenu={(e) => cm.current.show(e.originalEvent)}
                                                            emptyMessage={t("pricemanagement_page.price_checklist.empty")}>
                                                            {visibleColumns.map((col, i) => (
                                                                 <Column
                                                                      key={col.field + "_checklist_" + i}
                                                                      field={col.field}
                                                                      sortField={mapSortField(col.field) || col.field}
                                                                      header={col.header}
                                                                      body={
                                                                           col.field === "rule"
                                                                                ? (row) => formatRuleDisplay(row.rule)
                                                                                : col.field === "ppCurrency"
                                                                                  ? (row) => row.ppCurrency?.currency ?? ""
                                                                                  : col.field === "ppShop"
                                                                                    ? (row) => row.ppShop?.name ?? row.ppShop?.domain ?? ""
                                                                                  : col.field === "nettoFaktor" || col.field === "istNettoFaktor"
                                                                                    ? (row) => formatSentinelNumber(row[col.field])
                                                                                    : col.field === "crawlDate" || col.field === "createDate"
                                                                                      ? (row) => PPUtils.formatDateDE(row[col.field], true)
                                                                                      : undefined
                                                                      }
                                                                      sortable={col.filterable !== false}
                                                                      showFilterMenu={false}
                                                                      filter={col.filterable !== false}
                                                                      filterMatchModeOptions={[
                                                                           { label: "contains", value: FilterMatchMode.CONTAINS },
                                                                      ]}
                                                                      headerStyle={{
                                                                           border: "1px solid #ddd",
                                                                           backgroundColor: "#f8f9fa",
                                                                           fontWeight: "600",
                                                                           fontSize: "14px",
                                                                           padding: "12px 8px",
                                                                           textAlign: "center",
                                                                           color: "#495057",
                                                                           verticalAlign: "middle",
                                                                      }}
                                                                      filterHeaderStyle={{
                                                                           border: "1px solid #ddd",
                                                                           backgroundColor: "#ffffff",
                                                                           padding: "8px",
                                                                      }}
                                                                      filterElement={renderColumnFilter(col.field, col.header, col.filterable !== false)}
                                                                      style={{ minWidth: "9rem" }}
                                                                 />
                                                            ))}
                                                       </DataTable>
                                                  </div>
                                                  <div className="d-flex align-items-center justify-content-between mt-1">
                                                       <div>
                                                            <p className="text-muted small mb-0">{t("pricemanagement_page.price_checklist.ctrl_a_hint")}</p>
                                                            <p className="text-muted small mb-0">
                                                                 {t("pricemanagement_page.price_checklist.selected_info", {
                                                                      count: selectedRows?.length ?? 0,
                                                                      total: totalRecords,
                                                                 })}
                                                            </p>
                                                       </div>
                                                       <p className="text-muted small mb-0">
                                                            {(backgroundLoading || selectingAll) && (
                                                                 <i className="fas fa-circle-notch fa-spin me-1" />
                                                            )}
                                                            {tableData.length} / {totalRecords}
                                                       </p>
                                                  </div>
                                             </div>
                                        </div>
                                   </div>
                              </div>
                         </div>
                    </div>
               </div>

               <PPColumnSelectModal
                    instanceId="checklist_csm"
                    header={t("pricemanagement_page.price_checklist.column_picker_header")}
                    options={columns}
                    setOption={setTableColumns}
                    visible={columnModalVisible}
                    onHide={() => setColumnModalVisible(false)}
               />

               <Dialog
                    header={t("pricemanagement_page.price_checklist.edit_price_title")}
                    visible={editPriceOpen}
                    style={{ width: "32rem" }}
                    onHide={() => setEditPriceOpen(false)}
                    footer={
                         <>
                              <button type="button" className="btn btn-success me-2" onClick={requestSaveEditedPrice}>
                                   {t("pricemanagement_page.price_checklist.edit_price_apply")}
                              </button>
                              <button type="button" className="btn btn-secondary" onClick={() => setEditPriceOpen(false)}>
                                   {t("single.cancel")}
                              </button>
                         </>
                    }>
                    <p className="small text-muted">{t("pricemanagement_page.price_checklist.edit_price_hint")}</p>
                    {!editBulkMode && (
                         <div className="mb-2">
                              <label htmlFor="checklist-edit-price" className="form-label small">Preis</label>
                              <PPDecimalInput
                                   id="checklist-edit-price"
                                   className="form-control w-100"
                                   value={editPriceValue}
                                   onValueChange={(v) => setEditPriceValue(parseDecimalInputOrNull(v))}
                                   decimalsLimit={4}
                              />
                         </div>
                    )}
                    {editBulkMode && (
                         <>
                              <div className="mb-2">
                                   <label htmlFor="checklist-edit-netto" className="form-label small">Netto-Faktor</label>
                                   <PPDecimalInput
                                        id="checklist-edit-netto"
                                        className="form-control w-100"
                                        value={editNettoValue}
                                        onValueChange={(v) => setEditNettoValue(parseDecimalInputOrNull(v))}
                                        decimalsLimit={4}
                                   />
                              </div>
                              <div className="mb-2">
                                   <label htmlFor="checklist-edit-brutto" className="form-label small">Brutto-Faktor</label>
                                   <PPDecimalInput
                                        id="checklist-edit-brutto"
                                        className="form-control w-100"
                                        value={editBruttoValue}
                                        onValueChange={(v) => setEditBruttoValue(parseDecimalInputOrNull(v))}
                                        decimalsLimit={4}
                                   />
                              </div>
                              <div className="mb-2">
                                   <label htmlFor="checklist-edit-price-optional" className="form-label small">Preis (optional)</label>
                                   <PPDecimalInput
                                        id="checklist-edit-price-optional"
                                        className="form-control w-100"
                                        value={editPriceValue}
                                        onValueChange={(v) => setEditPriceValue(parseDecimalInputOrNull(v))}
                                        decimalsLimit={4}
                                   />
                              </div>
                         </>
                    )}
               </Dialog>

               <Dialog
                    header={t("single.confirmation")}
                    visible={confirmEditPriceOpen}
                    style={{ width: "28rem" }}
                    onHide={() => {
                         setConfirmEditPriceOpen(false)
                         setPendingEditPrice(null)
                    }}
                    footer={
                         <>
                              <button
                                   type="button"
                                   className="btn btn-secondary me-2"
                                   onClick={() => {
                                        setConfirmEditPriceOpen(false)
                                        setPendingEditPrice(null)
                                   }}>
                                   {t("single.cancel")}
                              </button>
                              <button type="button" className="btn btn-success" onClick={runConfirmedEditPrice} disabled={actionPending}>
                                   {t("pricemanagement_page.price_checklist.edit_price_apply")}
                              </button>
                         </>
                    }>
                    <p className="mb-0">
                         {!pendingEditPrice?.editBulkMode
                              ? t("pricemanagement_page.price_checklist.confirm_edit_price_single")
                              : editNettoValue != null || editBruttoValue != null
                                ? t("pricemanagement_page.price_checklist.confirm_edit_price_bulk_factor", {
                                       count: pendingEditPrice?.targets?.length ?? 0,
                                       factorType: editNettoValue != null ? "Netto" : "Brutto",
                                       factor: editNettoValue ?? editBruttoValue,
                                  })
                                : t("pricemanagement_page.price_checklist.confirm_edit_price_bulk", {
                                       count: pendingEditPrice?.targets?.length ?? 0,
                                  })}
                    </p>
               </Dialog>

               <Dialog
                    header={t("single.confirmation")}
                    visible={confirmApplyOpen}
                    style={{ width: "28rem" }}
                    onHide={() => setConfirmApplyOpen(false)}
                    footer={
                         <>
                              <button type="button" className="btn btn-secondary me-2" onClick={() => setConfirmApplyOpen(false)}>
                                   {t("single.cancel")}
                              </button>
                              <button type="button" className="btn btn-success" onClick={runAccept} disabled={actionPending}>
                                   {t("single.continue")}
                              </button>
                         </>
                    }>
                    <p className="mb-0">{t("pricemanagement_page.price_checklist.confirm_apply")}</p>
               </Dialog>

               <Dialog
                    header={t("single.confirmation")}
                    visible={confirmRejectOpen}
                    style={{ width: "28rem" }}
                    onHide={() => setConfirmRejectOpen(false)}
                    footer={
                         <>
                              <button type="button" className="btn btn-secondary me-2" onClick={() => setConfirmRejectOpen(false)}>
                                   {t("single.cancel")}
                              </button>
                              <button type="button" className="btn btn-danger" onClick={runReject} disabled={actionPending}>
                                   {t("single.continue")}
                              </button>
                         </>
                    }>
                    <p className="mb-0">{t("pricemanagement_page.price_checklist.confirm_reject")}</p>
               </Dialog>
          </>
     )
}
